/**
 * Parses the modernized DM voice: `**SCENE.**` and `**BEAT.**` headers, plus
 * `**⚠️ WARDED STAIR**` gate-block callouts. Streams safely (partial `**S`
 * won't crash — we just render the raw partial until the closing `**` arrives).
 */
function parseNarrative(text) {
  if (!text) return [];
  // Split on **HEADER.** markers. The regex captures the header including
  // trailing '.' so we can render it as a chip separate from the body.
  const parts = text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  const sections = [];
  let currentHeader = null;
  for (const part of parts) {
    const headerMatch = part.match(/^\*\*(.+?)\*\*$/);
    if (headerMatch) {
      currentHeader = headerMatch[1].trim().replace(/[.:]$/, "");
      continue;
    }
    const body = part.trim();
    if (body || currentHeader) {
      sections.push({ header: currentHeader, body });
      currentHeader = null;
    }
  }
  // If the whole text had no headers, return one plain section.
  if (sections.length === 0 && text.trim()) {
    return [{ header: null, body: text.trim() }];
  }
  return sections;
}

function headerStyle(header) {
  if (!header) return null;
  const upper = header.toUpperCase();
  if (upper.includes("WARDED STAIR") || upper.includes("⚠")) {
    return { color: "#ff6b3a", glow: "rgba(255,107,58,0.45)", accent: "#ff6b3a" };
  }
  if (upper === "SCENE") {
    return { color: "#d4af37", glow: "rgba(212,175,55,0.3)", accent: "#d4af37" };
  }
  if (upper === "BEAT") {
    return { color: "#8bb3ff", glow: "rgba(139,179,255,0.3)", accent: "#8bb3ff" };
  }
  return { color: "#a89373", glow: "rgba(168,147,115,0.2)", accent: "#a89373" };
}

import { useEffect, useRef } from "react";

export default function NarrativeBlock({ text, loading, streaming }) {
  const sections = parseNarrative(text);
  const blockRef = useRef(null);

  // Auto-scroll the block into view whenever streaming starts (mobile UX fix).
  // Uses "nearest" so we don't yank the whole page — just enough to make the
  // narrative visible when it starts filling in.
  useEffect(() => {
    if (streaming && blockRef.current) {
      try {
        blockRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
      } catch { /* older browsers — non-fatal */ }
    }
  }, [streaming]);

  return (
    <div ref={blockRef} className="panel p-6 md:p-8 min-h-[180px] narrative-scroll" data-testid="narrative-block">
      <div className="text-[10px] tracking-[0.4em] ash-text mb-3">— DUNGEON MASTER —</div>
      {loading ? (
        <div className="space-y-2">
          <div className="skeleton h-4 w-full" />
          <div className="skeleton h-4 w-11/12" />
          <div className="skeleton h-4 w-4/5" />
        </div>
      ) : (
        <div
          key={streaming ? "streaming" : text}
          className={`font-narrative text-lg md:text-xl leading-relaxed text-[#f0e6d2] ${streaming ? "" : "fade-up"}`}
          data-testid="narrative-text"
        >
          {sections.map((s, i) => {
            const st = headerStyle(s.header);
            return (
              <div key={i} className={i > 0 ? "mt-4" : ""}>
                {s.header && st && (
                  <div
                    className="inline-flex items-center text-[10px] tracking-[0.45em] mb-1.5 font-heading"
                    data-testid={`narrative-header-${s.header.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    style={{
                      color: st.color,
                      textShadow: `0 0 12px ${st.glow}`,
                      borderLeft: `2px solid ${st.accent}`,
                      paddingLeft: "8px",
                    }}
                  >
                    {s.header.toUpperCase()}
                  </div>
                )}
                {s.body && <p className="whitespace-pre-wrap">{s.body}</p>}
              </div>
            );
          })}
          {streaming && <span className="stream-caret" aria-hidden="true">▍</span>}
        </div>
      )}
    </div>
  );
}
